import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Sun,
    Wind,
    Droplets,
    Users,
    MapPin,
    Zap,
    CheckCircle,
    ArrowRight,
    X
} from 'lucide-react';
import { useWallet, WalletMultiButton } from '../context/SimpleWalletContext';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';

const CleanBackground = () => {
    return (
        <div className="fixed inset-0 overflow-hidden pointer-events-none bg-gray-50">
            <div className="absolute inset-0 opacity-5">
                <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-gray-300 rounded-full transform rotate-12"></div>
                <div className="absolute top-1/3 right-1/3 w-40 h-40 bg-gray-300 rounded-full transform -rotate-6"></div>
                <div className="absolute bottom-1/3 left-1/2 w-36 h-36 bg-gray-300 rounded-full transform rotate-3"></div>
                <div className="absolute top-1/2 right-1/4 w-28 h-28 bg-gray-300 rounded-full transform -rotate-12"></div>
            </div>
        </div>
    );
};

export default function Invest() {
    const location = useLocation();
    const { user } = useFirebaseAuth();
    const { connected, balance, updateBalance, addInvestment } = useWallet();
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [showInvestmentModal, setShowInvestmentModal] = useState(false);
    const [selectedProject, setSelectedProject] = useState<any>(null);
    const [investmentAmount, setInvestmentAmount] = useState<number>(0);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successData, setSuccessData] = useState<any>(null);

    // Handle URL-based filtering from dropdown menu
    useEffect(() => {
        const path = location.pathname;
        if (path.includes('/invest/solar')) {
            setSelectedCategory('solar');
        } else if (path.includes('/invest/wind')) {
            setSelectedCategory('wind');
        } else if (path.includes('/invest/hydro')) {
            setSelectedCategory('hydro');
        } else if (path.includes('/invest/community')) {
            setSelectedCategory('community');
        } else {
            setSelectedCategory('all');
        }
    }, [location.pathname]);


    // Investment functionality
    const handleInvestment = async (project: any, solAmount: number) => {

        if (!user) {
            alert('Please log in to make investments');
            return;
        }

        if (!connected) {
            alert('Please connect your wallet first');
            return;
        }

        if (solAmount < 1) {
            alert('Minimum investment is 1 SOL');
            return;
        }

        if (solAmount > balance) {
            alert('Insufficient balance');
            return;
        }

        // Get current user email for investment tracking
        const userEmail = localStorage.getItem('userEmail') || user.email || 'demo@example.com';

        // Simulate investment processing
        updateBalance(balance - solAmount);

        // Convert SOL to USD for display (using current rate 1 SOL = $190.67)
        const usdAmount = solAmount * 190.67;

        // Record the investment in localStorage
        addInvestment({
            projectId: project.id,
            projectName: project.title,
            solAmount: solAmount,
            usdAmount: usdAmount,
            userEmail: userEmail
        });

        // Show success modal
        const investmentData = {
            projectName: project.title,
            solAmount: solAmount,
            usdAmount: usdAmount,
            solPaid: solAmount.toFixed(4),
            transaction: `demo_tx_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        };

        setSuccessData(investmentData);
        setShowSuccessModal(true);
        setShowInvestmentModal(false);
    };

    const investmentCategories = [
        { id: 'all', label: 'All Projects', icon: Zap, count: 13 },
        { id: 'solar', label: 'Solar', icon: Sun, count: 5 },
        { id: 'wind', label: 'Wind', icon: Wind, count: 4 },
        { id: 'hydro', label: 'Hydro', icon: Droplets, count: 2 },
        { id: 'community', label: 'Community', icon: Users, count: 1 },
    ];

    const projects = [
        {
            id: 1,
            title: "Solar Farm Karachi",
            category: "solar",
            location: "Karachi, Pakistan",
            capacity: "50 MW",
            investment: "$100 - $10,000",
            returns: "12-15% APY",
            timeline: "24 months",
            status: "Active",
            description: "Large-scale solar farm providing clean energy to Karachi's industrial sector",
            image: "/assets/images/solar-panel-farm.jpg",
            progress: 75,
            investors: 1247,
            raised: "$2.4M",
            target: "$3.2M"
        },
        {
            id: 2,
            title: "Wind Energy Hub",
            category: "wind",
            location: "Sindh Province",
            capacity: "30 MW",
            investment: "$200 - $5,000",
            returns: "10-12% APY",
            timeline: "18 months",
            status: "Active",
            description: "Wind turbines harnessing coastal winds for sustainable energy generation",
            image: "/assets/images/wind-farm-landscape.jpg",
            progress: 60,
            investors: 892,
            raised: "$1.8M",
            target: "$3.0M"
        },
        {
            id: 3,
            title: "Community Solar Initiative",
            category: "community",
            location: "Lahore, Pakistan",
            capacity: "10 MW",
            investment: "$50 - $1,000",
            returns: "8-10% APY",
            timeline: "12 months",
            status: "Active",
            description: "Community-driven solar project empowering local neighborhoods",
            image: "/assets/images/renewable-energy-grid.jpg",
            progress: 45,
            investors: 2156,
            raised: "$1.2M",
            target: "$2.7M"
        },
        {
            id: 4,
            title: "Hydroelectric Plant",
            category: "hydro",
            location: "Northern Pakistan",
            capacity: "25 MW",
            investment: "$500 - $15,000",
            returns: "15-18% APY",
            timeline: "36 months",
            status: "Active",
            description: "Clean hydroelectric power utilizing mountain water resources",
            image: "/assets/images/clean-energy-technology.jpg",
            progress: 30,
            investors: 456,
            raised: "$0.9M",
            target: "$3.0M"
        },
        // Additional Solar Projects
        {
            id: 5,
            title: "Solar Park Lahore",
            category: "solar",
            location: "Lahore, Pakistan",
            capacity: "35 MW",
            investment: "$150 - $8,000",
            returns: "11-14% APY",
            timeline: "20 months",
            status: "Active",
            description: "Urban solar park providing clean energy to Lahore's residential areas",
            image: "/assets/images/solar-energy-panels.jpg",
            progress: 85,
            investors: 1834,
            raised: "$2.1M",
            target: "$2.5M"
        },
        {
            id: 6,
            title: "Desert Solar Farm",
            category: "solar",
            location: "Thar Desert",
            capacity: "75 MW",
            investment: "$300 - $20,000",
            returns: "13-16% APY",
            timeline: "30 months",
            status: "Active",
            description: "Large-scale desert solar farm with advanced tracking technology",
            image: "/assets/images/solar-panel-farm.jpg",
            progress: 40,
            investors: 2341,
            raised: "$3.2M",
            target: "$8.0M"
        },
        {
            id: 7,
            title: "Rooftop Solar Initiative",
            category: "solar",
            location: "Islamabad",
            capacity: "15 MW",
            investment: "$75 - $3,000",
            returns: "9-12% APY",
            timeline: "15 months",
            status: "Active",
            description: "Distributed rooftop solar across government buildings",
            image: "/assets/images/renewable-energy-grid.jpg",
            progress: 70,
            investors: 987,
            raised: "$1.8M",
            target: "$2.6M"
        },
        {
            id: 8,
            title: "Solar Storage Hub",
            category: "solar",
            location: "Karachi Port",
            capacity: "40 MW",
            investment: "$200 - $12,000",
            returns: "12-15% APY",
            timeline: "24 months",
            status: "Active",
            description: "Solar farm with integrated battery storage system",
            image: "/assets/images/clean-energy-technology.jpg",
            progress: 55,
            investors: 1456,
            raised: "$2.8M",
            target: "$5.1M"
        },
        // Additional Wind Projects
        {
            id: 9,
            title: "Coastal Wind Farm",
            category: "wind",
            location: "Gwadar Port",
            capacity: "45 MW",
            investment: "$250 - $10,000",
            returns: "11-13% APY",
            timeline: "22 months",
            status: "Active",
            description: "Offshore wind farm harnessing coastal winds",
            image: "/assets/images/wind-farm-landscape.jpg",
            progress: 65,
            investors: 1678,
            raised: "$2.3M",
            target: "$3.5M"
        },
        {
            id: 10,
            title: "Mountain Wind Project",
            category: "wind",
            location: "Hunza Valley",
            capacity: "20 MW",
            investment: "$180 - $6,000",
            returns: "10-12% APY",
            timeline: "18 months",
            status: "Active",
            description: "High-altitude wind turbines in mountainous terrain",
            image: "/assets/images/wind-turbine-sunset.jpg",
            progress: 80,
            investors: 1123,
            raised: "$1.9M",
            target: "$2.4M"
        },
        {
            id: 11,
            title: "Urban Wind Towers",
            category: "wind",
            location: "Rawalpindi",
            capacity: "12 MW",
            investment: "$120 - $4,000",
            returns: "9-11% APY",
            timeline: "14 months",
            status: "Active",
            description: "Small-scale wind turbines for urban energy generation",
            image: "/assets/images/wind-turbine-closeup.jpg",
            progress: 90,
            investors: 756,
            raised: "$1.4M",
            target: "$1.6M"
        },
        {
            id: 12,
            title: "Desert Wind Energy",
            category: "wind",
            location: "Cholistan Desert",
            capacity: "60 MW",
            investment: "$350 - $15,000",
            returns: "12-14% APY",
            timeline: "28 months",
            status: "Active",
            description: "Desert wind farm with advanced turbine technology",
            image: "/assets/images/wind-farm-landscape.jpg",
            progress: 35,
            investors: 2890,
            raised: "$3.8M",
            target: "$10.8M"
        },
        // Additional Hydro Project
        {
            id: 13,
            title: "River Hydro Station",
            category: "hydro",
            location: "Indus River",
            capacity: "40 MW",
            investment: "$600 - $18,000",
            returns: "16-20% APY",
            timeline: "42 months",
            status: "Active",
            description: "Run-of-river hydroelectric power station",
            image: "/assets/images/clean-energy-technology.jpg",
            progress: 25,
            investors: 678,
            raised: "$1.2M",
            target: "$4.8M"
        }
    ];

    const filteredProjects = selectedCategory === 'all'
        ? projects
        : projects.filter(project => project.category === selectedCategory);

    return (
        <div className="min-h-screen bg-gray-50">
            <CleanBackground />

            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
                {/* Header Section */}
                <div className="text-center mb-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
                            Invest in <span className="text-green-600">Clean Energy</span>
                        </h1>
                        <p className="text-lg max-w-3xl mx-auto text-gray-600 mb-4">
                            Co-own clean energy plants starting from $100. Explore active projects,
                            projected returns, and real-time metrics.
                        </p>

                        {/* Wallet Connection Status */}
                        {user && (
                            <div className="mb-6">
                                {connected ? (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        className="inline-flex items-center space-x-3 bg-green-50 px-6 py-3 rounded-full border border-green-200"
                                    >
                                        <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                                        <span className="text-green-700 font-medium">
                                            Wallet Connected • {balance.toFixed(2)} SOL Available
                                        </span>
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        className="inline-flex items-center space-x-3 bg-yellow-50 px-6 py-3 rounded-full border border-yellow-200"
                                    >
                                        <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                                        <span className="text-yellow-700 font-medium">
                                            Connect Wallet to Invest
                                        </span>
                                        <WalletMultiButton />
                                    </motion.div>
                                )}
                            </div>
                        )}

                        {/* Current Filter Status */}
                        <div className="mb-8">
                            <div className="inline-flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-200">
                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                <span className="text-sm text-gray-600">
                                    Showing {filteredProjects.length} {selectedCategory === 'all' ? 'projects' : `${selectedCategory} projects`}
                                </span>
                            </div>
                        </div>
                    </motion.div>

                    {/* Category Filter */}
                    <motion.div
                        className="flex flex-wrap justify-center gap-4 mb-12"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.6 }}
                    >
                        {investmentCategories.map((category) => {
                            const Icon = category.icon;
                            return (
                                <button
                                    key={category.id}
                                    onClick={() => setSelectedCategory(category.id)}
                                    className={`flex items-center space-x-2 px-6 py-3 rounded-lg transition-all duration-200 ${selectedCategory === category.id
                                        ? 'bg-green-600 text-white shadow-lg'
                                        : 'bg-white text-gray-700 hover:bg-gray-100 shadow-sm border border-gray-200'
                                        }`}
                                >
                                    <Icon className="w-5 h-5" />
                                    <span className="font-medium">{category.label}</span>
                                    <span className="text-sm opacity-75">({category.count})</span>
                                </button>
                            );
                        })}
                    </motion.div>
                </div>

                {/* Projects Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {filteredProjects.map((project, index) => (
                        <motion.div
                            key={project.id}
                            className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1, duration: 0.6 }}
                            whileHover={{ y: -5, scale: 1.02 }}
                        >
                            {/* Project Image */}
                            <div className="h-48 bg-gradient-to-br from-green-100 to-blue-100 relative overflow-hidden">
                                <img
                                    src={project.image}
                                    alt={project.title}
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute top-4 right-4">
                                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${project.status === 'Active'
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-gray-100 text-gray-800'
                                        }`}>
                                        {project.status}
                                    </span>
                                </div>
                            </div>

                            {/* Project Content */}
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-xl font-bold text-gray-900">{project.title}</h3>
                                    <div className="flex items-center text-sm text-gray-500">
                                        <MapPin className="w-4 h-4 mr-1" />
                                        {project.location}
                                    </div>
                                </div>

                                <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                                    {project.description}
                                </p>

                                {/* Project Stats */}
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-green-600">{project.capacity}</div>
                                        <div className="text-xs text-gray-500">Capacity</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-blue-600">{project.returns}</div>
                                        <div className="text-xs text-gray-500">Expected Returns</div>
                                    </div>
                                </div>

                                {/* Investment Range */}
                                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-gray-600">Investment Range:</span>
                                        <span className="font-semibold text-gray-900">{project.investment}</span>
                                    </div>
                                    <div className="flex items-center justify-between text-sm mt-1">
                                        <span className="text-gray-600">Timeline:</span>
                                        <span className="font-semibold text-gray-900">{project.timeline}</span>
                                    </div>
                                </div>

                                {/* Progress Bar */}
                                <div className="mb-4">
                                    <div className="flex justify-between text-sm text-gray-600 mb-2">
                                        <span>Progress</span>
                                        <span>{project.progress}%</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div
                                            className="bg-green-600 h-2 rounded-full transition-all duration-500"
                                            style={{ width: `${project.progress}%` }}
                                        ></div>
                                    </div>
                                </div>

                                {/* Investment Stats */}
                                <div className="grid grid-cols-3 gap-2 mb-6 text-center">
                                    <div>
                                        <div className="text-lg font-bold text-gray-900">{project.investors}</div>
                                        <div className="text-xs text-gray-500">Investors</div>
                                    </div>
                                    <div>
                                        <div className="text-lg font-bold text-green-600">{project.raised}</div>
                                        <div className="text-xs text-gray-500">Raised</div>
                                    </div>
                                    <div>
                                        <div className="text-lg font-bold text-gray-900">{project.target}</div>
                                        <div className="text-xs text-gray-500">Target</div>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex space-x-3">
                                    <motion.button
                                        className="flex-1 bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={() => {
                                            if (!user) {
                                                alert('Please log in to make investments');
                                                return;
                                            }
                                            if (!connected) {
                                                alert('Please connect your wallet first');
                                                return;
                                            }
                                            setSelectedProject(project);
                                            setInvestmentAmount(1); // Set default investment amount
                                            setShowInvestmentModal(true);
                                        }}
                                    >
                                        <Zap className="w-4 h-4" />
                                        <span>Invest Now</span>
                                    </motion.button>
                                    <motion.button
                                        className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2"
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                    >
                                        <ArrowRight className="w-4 h-4" />
                                        <span>Details</span>
                                    </motion.button>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Call to Action */}
                <motion.div
                    className="text-center mt-20"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8, duration: 0.6 }}
                >
                    <div className="bg-white p-8 md:p-12 rounded-2xl shadow-lg border border-gray-200 max-w-4xl mx-auto">
                        <h2 className="text-3xl font-bold mb-4 text-gray-900">
                            Ready to Start Investing?
                        </h2>
                        <p className="text-lg text-gray-600 mb-8">
                            Join thousands of investors building a sustainable future through clean energy investments.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <motion.button
                                className="bg-green-600 text-white px-8 py-4 rounded-lg font-medium hover:bg-green-700 transition-colors"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                Get Started Today
                            </motion.button>
                            <motion.button
                                className="bg-gray-100 text-gray-700 px-8 py-4 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                Learn More
                            </motion.button>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Investment Modal */}
            <AnimatePresence>
                {showInvestmentModal && selectedProject && (
                    <motion.div
                        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setShowInvestmentModal(false)}
                    >
                        <motion.div
                            className="bg-white rounded-2xl p-8 max-w-md w-full mx-4"
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-2xl font-bold text-gray-900">Invest in {selectedProject.title}</h3>
                                <button
                                    onClick={() => setShowInvestmentModal(false)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>

                            <div className="mb-6">
                                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                                    <div className="flex justify-between text-sm mb-2">
                                        <span className="text-gray-600">Project:</span>
                                        <span className="font-semibold">{selectedProject.title}</span>
                                    </div>
                                    <div className="flex justify-between text-sm mb-2">
                                        <span className="text-gray-600">Expected Returns:</span>
                                        <span className="font-semibold text-green-600">{selectedProject.returns}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Timeline:</span>
                                        <span className="font-semibold">{selectedProject.timeline}</span>
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Investment Amount (SOL)
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        max={balance}
                                        step="0.1"
                                        value={investmentAmount}
                                        onChange={(e) => {
                                            const newAmount = parseFloat(e.target.value) || 0;
                                            setInvestmentAmount(newAmount);
                                        }}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        placeholder="Enter amount in SOL"
                                    />
                                    <div className="text-sm text-gray-500 mt-1">
                                        Available: {balance.toFixed(2)} SOL
                                    </div>
                                    <div className="text-xs text-blue-600 mt-1">
                                        Current amount: {investmentAmount} SOL
                                    </div>
                                </div>

                                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">USD Equivalent:</span>
                                        <span className="font-semibold text-blue-600">
                                            ${(investmentAmount * 190.67).toFixed(2)}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="flex space-x-3">
                                <button
                                    onClick={() => setShowInvestmentModal(false)}
                                    className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => handleInvestment(selectedProject, investmentAmount)}
                                    disabled={investmentAmount < 1 || investmentAmount > balance}
                                    className="flex-1 bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                                >
                                    Invest {investmentAmount} SOL
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Success Modal */}
            <AnimatePresence>
                {showSuccessModal && (
                    <motion.div
                        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setShowSuccessModal(false)}
                    >
                        <motion.div
                            className="bg-white rounded-2xl p-8 max-w-md w-full mx-4"
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="text-center">
                                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <CheckCircle className="w-8 h-8 text-green-600" />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-2">Investment Successful!</h3>
                                <p className="text-gray-600 mb-6">
                                    Your investment in {successData?.projectName || 'this project'} has been processed successfully.
                                </p>

                                <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                                    <div className="flex justify-between text-sm mb-2">
                                        <span className="text-gray-600">Amount Invested:</span>
                                        <span className="font-semibold text-green-600">
                                            {successData.solPaid} SOL
                                        </span>
                                    </div>
                                    <div className="flex justify-between text-sm mb-2">
                                        <span className="text-gray-600">USD Value:</span>
                                        <span className="font-semibold text-blue-600">
                                            ${successData.usdAmount.toFixed(2)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Transaction ID:</span>
                                        <span className="font-mono text-xs text-gray-800">
                                            {successData.transaction}
                                        </span>
                                    </div>
                                </div>

                                <button
                                    onClick={() => setShowSuccessModal(false)}
                                    className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 transition-colors"
                                >
                                    Continue Investing
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
